import {
	Box,
	Button,
	Inline,
	Icons,
	Stack,
	Heading,
	Text,
	Col,
	Divider,
	Grid,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import { useXHR } from "@sembark-travel/xhr"
import {
	Form,
	PhoneInputField,
	TextInputField,
	withServerErrors,
	SubmissionError,
	RadioInputField,
	validateFormValues,
	GetFieldValue,
	useFieldValue,
	PhoneNumberValidator,
	SwitchInputField,
	isTruthy,
	CheckboxInputField,
	SelectField,
} from "@sembark-travel/ui-form"
import React, { useMemo, useState } from "react"
import { $PropertyType, Optional } from "utility-types"
import * as Validator from "yup"
import { ISupplier } from "./store"
import {
	FeatureFlag,
	PERMISSIONS,
	useCheckPermissions,
	useHasFeatureFlag,
} from "../Auth"
import {
	SelectActivityPaymentPreferences,
	SelectHotelPaymentPreferences,
	SelectTransportPaymentPreferences,
} from "../SupplierPaymentPreferences"

const validationSchema = Validator.object().shape({
	provider_type: Validator.string().required("Please select a provider type"),
	name: Validator.string()
		.required("Please provide a name for the service provider")
		.max(191, "Value can not be longer then 191 characters"),
	contact_name: Validator.string()
		.when("provider_type", ([providerType], schema: Validator.StringSchema) => {
			if (providerType === "company") {
				return schema.required("Please provider the name of the contact")
			}
			return schema.nullable()
		})
		.max(191, "Value can not be longer then 191 characters"),
	contact_email: Validator.string()
		.email("Please provide a valid email address")
		.nullable()
		.max(191, "Value can not be longer then 191 characters"),
	contact_phone_numbers: Validator.array()
		.of(PhoneNumberValidator())
		.min(1, "Please provide atleast one phone number"),
})

const validate = validateFormValues(validationSchema)

type TProviderType = "solo_driver" | "company"

type NewItemCredentials<TSupplier = ISupplier | number> = {
	name: string
	contact_name?: string
	contact_phone_numbers: Array<{
		number: string
		country_code: string
		phone_number: string
	}>
	contact_email: string
	provider_type: TProviderType
	supplies_cabs: 0 | 1
	supplies_travel_activities: 0 | 1
	supplies_hotels: 0 | 1
	merge_if_exist: 0 | 1
	transport_supplier_payment_preference?: TSupplier | null
	activity_supplier_payment_preference?: TSupplier | null
	hotel_supplier_payment_preference?: TSupplier | null
}

const INITIAL_VALUES: NewItemCredentials = {
	name: "",
	contact_name: "",
	contact_phone_numbers: [
		{
			number: "",
			country_code: "IN",
			phone_number: "+91",
		},
	],
	contact_email: "",
	provider_type: "company",
	supplies_cabs: 0,
	supplies_travel_activities: 0,
	supplies_hotels: 0,
	merge_if_exist: 0,
	transport_supplier_payment_preference: null,
	activity_supplier_payment_preference: null,
	hotel_supplier_payment_preference: null,
}

interface NewItemProps {
	onCancel?: () => void
	onSuccess: (data: ISupplier) => void
	initialValues?: Optional<NewItemCredentials>
	keepInitialSuppliesSelected?: boolean
}

export function NewItemForm({
	onCancel,
	onSuccess,
	initialValues: propsInitialValue,
	keepInitialSuppliesSelected,
}: Readonly<NewItemProps>) {
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	const hasTravelActivities = useHasFeatureFlag("travel_activities")
	const [showMergeCheckbox, setShowMergeCheckbox] = useState(false)
	const [initialValues] = useState(() => {
		let initialValues: NewItemCredentials = INITIAL_VALUES
		if (propsInitialValue) {
			initialValues = {
				...initialValues,
				...propsInitialValue,
			}
		}
		return {
			...initialValues,
			supplies_cabs: (initialValues.supplies_cabs || !hasTravelActivities
				? 1
				: 0) as 0 | 1,
			supplies_travel_activities: (initialValues.supplies_travel_activities
				? 1
				: 0) as 0 | 1,
			supplies_hotels: (initialValues.supplies_hotels ? 1 : 0) as 0 | 1,
		}
	})
	return (
		<Form<NewItemCredentials>
			initialValues={initialValues}
			validate={validate}
			onSubmit={withServerErrors(
				async ({
					provider_type,
					supplies_cabs,
					supplies_travel_activities,
					supplies_hotels,
					merge_if_exist,
					...data
				}: NewItemCredentials) => {
					if (!hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS)) {
						if ("activity_supplier_payment_preference" in data)
							delete data.activity_supplier_payment_preference
						if ("transport_supplier_payment_preference" in data)
							delete data.transport_supplier_payment_preference
					}
					if (!hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS)) {
						if ("hotel_supplier_payment_preference" in data)
							delete data.hotel_supplier_payment_preference
					}

					if (
						!isTruthy(supplies_travel_activities) &&
						!isTruthy(supplies_cabs) &&
						!isTruthy(supplies_hotels)
					) {
						throw new Error(
							"Please select atleast one of the supplied services by this supplier"
						)
					}
					if ("activity_supplier_payment_preference" in data) {
						data.activity_supplier_payment_preference = (
							data.activity_supplier_payment_preference as ISupplier
						)?.id
					}
					if ("transport_supplier_payment_preference" in data) {
						data.transport_supplier_payment_preference = (
							data.transport_supplier_payment_preference as ISupplier
						)?.id
					}
					if ("hotel_supplier_payment_preference" in data) {
						data.hotel_supplier_payment_preference = (
							data.hotel_supplier_payment_preference as ISupplier
						)?.id
					}
					const payload = {
						...data,
						supplies_cabs,
						supplies_travel_activities,
						supplies_hotels,
						merge_if_exist: showMergeCheckbox
							? merge_if_exist
								? 1
								: 0
							: undefined,
						solo_driver:
							provider_type === "solo_driver" && isTruthy(supplies_cabs)
								? 1
								: 0,
					}
					const resp = await xhr
						.post("/transport-service-providers", payload)
						.then((resp) => resp.data)
						.catch((err) => {
							if (err.response.status === 409) {
								setShowMergeCheckbox(true)
							}
							throw err
						})
					onSuccess(resp.data)
				}
			)}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Heading as="h4">
									<Icons.Cog size="6" /> Supplied Services
								</Heading>
								<Text color="muted">
									Select the types of services supplied by this supplier.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Inline gap="4" flexWrap="wrap">
								{hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS) ? (
									<>
										<Box
											paddingY="2"
											paddingX="4"
											borderWidth="1"
											rounded="md"
											bgColor="subtle"
										>
											<SwitchInputField
												name="supplies_cabs"
												label="Cabs"
												onChange={(value) => {
													form.batch(() => {
														if (value) {
															form.change("supplies_cabs", 1)
														} else {
															form.change("provider_type", "company")
															form.change("supplies_cabs", 0)
														}
													})
												}}
											/>
										</Box>
										<FeatureFlag flag="travel_activities">
											<Box
												paddingY="2"
												paddingX="4"
												borderWidth="1"
												rounded="md"
												bgColor="subtle"
											>
												<SwitchInputField
													name="supplies_travel_activities"
													label="Travel Activities"
												/>
											</Box>
										</FeatureFlag>
									</>
								) : null}

								{hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS) ? (
									<Box
										paddingY="2"
										paddingX="4"
										borderWidth="1"
										rounded="md"
										bgColor="subtle"
									>
										<SwitchInputField name="supplies_hotels" label="Hotels" />
									</Box>
								) : null}
							</Inline>
						</Col>
					</Grid>
					<Divider />

					<GetFieldValue<
						(typeof initialValues)["supplies_cabs"]
					> name="supplies_cabs">
						{({ value }) =>
							!isTruthy(value) ? null : (
								<>
									<Grid gap="4">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading as="h4">
													<Icons.Tags size="6" /> Cab Supplier Type
												</Heading>
												<Text color="muted">
													Please select if this is a transporter company or a
													single driver.
												</Text>
											</Stack>
										</Col>
										<Col>
											<Grid gap="4">
												<Col xs={12} sm>
													<ProviderTypeOption
														option="company"
														name="provider_type"
														label={
															<>
																<Box display="inlineBlock">
																	Transporter Company
																</Box>{" "}
																<Icons.UserGroup size="5" color="muted" />
															</>
														}
														help="Please select this if this a multi driver/cab transporter company."
													/>
												</Col>
												<Col xs={12} sm>
													<ProviderTypeOption
														option="solo_driver"
														name="provider_type"
														label={
															<>
																<Box display="inlineBlock">Single Driver</Box>{" "}
																<Icons.SteeringWheel size="5" color="muted" />
															</>
														}
														help="Please select this if the driver is the only person in this company."
													/>
												</Col>
											</Grid>
										</Col>
									</Grid>
									<Divider />
								</>
							)
						}
					</GetFieldValue>
					<GetFieldValue<
						NewItemCredentials["provider_type"]
					> name="provider_type">
						{({ value: provider_type }) =>
							provider_type === "company" ? (
								<>
									<Grid gap="4">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading as="h4">
													<Icons.UserGroup size="6" /> Company Details
												</Heading>
												<Text color="muted">
													Please provide the company/agent details e.g. name.
												</Text>
											</Stack>
										</Col>
										<Col>
											<Stack gap="4">
												<TextInputField
													maxWidth="sm"
													label="Name"
													name="name"
													placeholder="Sample Transportations"
													required
													type="text"
												/>
												{showMergeCheckbox ? (
													<CheckboxInputField
														name="merge_if_exist"
														label="Merge with existing company"
														help="Once you check it, it will be merged with the existing one, including all details."
													/>
												) : null}
											</Stack>
										</Col>
									</Grid>
									<Divider />
									<Grid gap="4">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading as="h4">
													<Icons.User size="6" /> Contact Details
												</Heading>
												<Text color="muted">
													Please provide a contact's details for the service
													provide. You can add more contact later.
												</Text>
											</Stack>
										</Col>
										<Col>
											<Stack gap="4">
												<TextInputField
													maxWidth="sm"
													label="Contact Name"
													name="contact_name"
													placeholder="Sample Name"
													required
													type="text"
												/>
												<Grid gap="4">
													<Col>
														<PhoneInputField
															label="Contact Phone Number(s)"
															name="contact_phone_numbers"
															required
															multi
														/>
													</Col>
													<Col>
														<TextInputField
															maxWidth="xs"
															label="Contact Email"
															secondaryLabel="optional"
															name="contact_email"
															placeholder="domain@example.com"
															type="email"
															style={{ minWidth: "200px" }}
														/>
													</Col>
												</Grid>
											</Stack>
										</Col>
									</Grid>
								</>
							) : (
								<Grid gap="4">
									<Col sm={12} md={4}>
										<Stack gap="1">
											<Heading as="h4">
												<Icons.User size="6" /> Driver Details
											</Heading>
											<Text color="muted">
												Please provide name and contact details of provider.
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap="4">
											<Grid gap="4">
												<Col xs={12} sm>
													<TextInputField
														label="Name"
														name="name"
														placeholder="Mahendra Singh G"
														required
														type="text"
														maxWidth="sm"
													/>
												</Col>
												<Col>
													<PhoneInputField
														label="Contact Phone Number(s)"
														name="contact_phone_numbers"
														required
														multi
													/>
												</Col>
											</Grid>
											<TextInputField
												label="Contact Email"
												secondaryLabel="optional"
												name="contact_email"
												placeholder="domain@example.com"
												type="email"
												maxWidth="xs"
											/>
											{showMergeCheckbox ? (
												<CheckboxInputField
													name="merge_if_exist"
													label="Merge with existing Driver"
													help="Once you check it, it will be merged with the existing one, including all details."
												/>
											) : null}
										</Stack>
									</Col>
								</Grid>
							)
						}
					</GetFieldValue>
					<Divider sm />
					<SupplierPaymentPreferences />
					<Grid gap="4">
						<Col sm={12} md={{ offset: 4, span: 8 }}>
							<Stack gap="4" paddingLeft={{ md: "4" }}>
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting} size="lg">
										{submitting ? "Saving..." : "Save Details"}
									</Button>
									{onCancel ? (
										<Button
											onClick={onCancel}
											size="lg"
											level="tertiary"
											disabled={submitting}
										>
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}

function ProviderTypeOption({
	name,
	option,
	label,
	...props
}: Readonly<{
	name: string
	label: React.ReactNode
	help?: React.ReactNode
	option: TProviderType
}>) {
	const { value } = useFieldValue<NewItemCredentials["provider_type"]>(name)
	const isSelected = value === option
	return (
		<Box
			padding="4"
			borderWidth="1"
			rounded="lg"
			boxShadow={isSelected ? "lg" : "inner"}
			bgColor="subtle"
			as="label"
			cursor="pointer"
			display="block"
			borderColor={isSelected ? "primary" : "default"}
			marginBottom="4"
		>
			<RadioInputField
				name={name}
				value={option}
				label={label as never}
				{...props}
			/>
		</Box>
	)
}

export function AddTransportServiceProviderDialog({
	open,
	onClose,
	onSuccess,
	...props
}: Omit<NewItemProps, "onCancel"> & {
	open: boolean
	onClose: () => void
	onSuccess?: (tsp: ISupplier) => void
	initialValues?: $PropertyType<NewItemProps, "initialValues">
}) {
	return (
		<Dialog open={open} onClose={onClose} title="Add New Supplier">
			<Dialog.Body>
				<NewItemForm
					{...props}
					onSuccess={onSuccess || onClose}
					onCancel={onClose}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

function SupplierPaymentPreferences() {
	const { hasPermission } = useCheckPermissions()
	const { value: supplies_cabs } =
		useFieldValue<NewItemCredentials["supplies_cabs"]>("supplies_cabs")
	const { value: supplies_hotels } =
		useFieldValue<NewItemCredentials["supplies_hotels"]>("supplies_hotels")
	const { value: supplies_travel_activities } = useFieldValue<
		NewItemCredentials["supplies_travel_activities"]
	>("supplies_travel_activities")

	const showCabPreferences = isTruthy(supplies_cabs)
	const showActivitiesPreferences = isTruthy(supplies_travel_activities)
	const showHotelPreferences = isTruthy(supplies_hotels)

	const showPaymentPreference = useMemo(
		() =>
			showCabPreferences || showActivitiesPreferences || showHotelPreferences,
		[showCabPreferences, showActivitiesPreferences, showHotelPreferences]
	)
	if (!showPaymentPreference) return null

	return (
		<>
			<Grid gap="4">
				<Col sm={12} md={4}>
					<Stack gap="1">
						<Heading as="h4">
							<Icons.BankNotes size="6" /> Supplier Payment Preferences
						</Heading>
						<Text color="muted">
							Please provide the supplier payment preferences details.
						</Text>
					</Stack>
				</Col>
				<Col>
					<Grid gap="4">
						{hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS) &&
						showCabPreferences ? (
							<Col sm={12} lg={5}>
								<SelectField
									select={SelectTransportPaymentPreferences}
									name="transport_supplier_payment_preference"
									label="Cab Payment Preferences"
									required
									fetchOnMount
									multiple={false}
									placeholder="Type to search..."
								/>
							</Col>
						) : null}
						{hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS) &&
						showActivitiesPreferences ? (
							<Col sm={12} lg={5}>
								<SelectField
									select={SelectActivityPaymentPreferences}
									name="activity_supplier_payment_preference"
									label="Activities Payment Preferences"
									required
									fetchOnMount
									multiple={false}
									placeholder="Type to search..."
								/>
							</Col>
						) : null}
						{hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS) &&
						showHotelPreferences ? (
							<Col sm={12} lg={5}>
								<SelectField
									select={SelectHotelPaymentPreferences}
									name="hotel_supplier_payment_preference"
									label="Hotel Payment Preferences"
									required
									fetchOnMount
									multiple={false}
									placeholder="Type to search..."
								/>
							</Col>
						) : null}
					</Grid>
				</Col>
			</Grid>
			<Divider />
		</>
	)
}
